import React, { useMemo } from "react";
import { Anchor, Card, Col, Divider, Grid, Row, Typography } from "antd";
import "./style.css";

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

type Block =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "note"; text: string };

type Section = {
  id: string;
  title: string;
  blocks: Block[];
};

interface PrivacyPolicyProps {
  lastUpdated?: string;
  contactEmail?: string;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({
  lastUpdated = "22/08/2025",
  contactEmail = "support@platformb2b.com",
}) => {
  const screens = useBreakpoint();

  const sections: Section[] = useMemo(
    () => [
      {
        id: "gioi-thieu",
        title: "1. Giới thiệu",
        blocks: [
          {
            type: "paragraph",
            text: "Chính sách này giải thích cách chúng tôi thu thập, sử dụng, lưu trữ và bảo vệ dữ liệu cá nhân của bạn khi sử dụng nền tảng AI Procurement. Chúng tôi cam kết tuân thủ các tiêu chuẩn quốc tế về bảo mật thông tin và bảo vệ quyền riêng tư. Tất cả các thông tin người dùng cung cấp được phép dùng để phục vụ cho công tác phát triển nền tảng.",
          },
        ],
      },
      {
        id: "pham-vi-ap-dung",
        title: "2. Phạm vi áp dụng",
        blocks: [
          {
            type: "paragraph",
            text: "Chính sách này áp dụng cho tất cả người dùng, khách hàng, nhà cung cấp, đối tác khi truy cập và sử dụng nền tảng AI Procurement qua website, ứng dụng, API hoặc các kênh tích hợp khác.",
          },
        ],
      },
      {
        id: "du-lieu-thu-thap",
        title: "3. Dữ liệu chúng tôi thu thập",
        blocks: [
          {
            type: "list",
            items: [
              "Thông tin định danh: họ tên, email, số điện thoại, chức vụ, doanh nghiệp.",
              "Thông tin đăng nhập: tài khoản, mật khẩu (được mã hoá).",
              "Thông tin tài khoản thanh toán",
              "Dữ liệu giao dịch: yêu cầu báo giá (RFQ), báo giá.",
              "Thông tin kỹ thuật: địa chỉ IP, trình duyệt, thiết bị, nhật ký truy cập.",
              "Dữ liệu AI/ML: thông tin được xử lý để gợi ý tối ưu chi phí, lựa chọn nhà cung cấp, phân tích rủi ro.",
            ],
          },
        ],
      },
      {
        id: "muc-dich-su-dung",
        title: "4. Mục đích sử dụng dữ liệu",
        blocks: [
          {
            type: "list",
            items: [
              "Cung cấp dịch vụ mua hàng thông minh dựa trên AI.",
              "Quản lý giao dịch, thanh toán.",
              "Tối ưu chi phí và gợi ý lựa chọn nhà cung cấp.",
              "Nâng cao trải nghiệm người dùng thông qua phân tích hành vi.",
              "Đảm bảo an toàn, ngăn chặn gian lận và tuân thủ pháp luật.",
              "Phát triển nền tảng.",
            ],
          },
        ],
      },
      {
        id: "chia-se-du-lieu",
        title: "5. Chia sẻ dữ liệu",
        blocks: [
          {
            type: "paragraph",
            text: "Chúng tôi chỉ chia sẻ dữ liệu với:",
          },
          {
            type: "list",
            items: [
              "Nhà cung cấp, đối tác có liên quan trực tiếp đến giao dịch của bạn.",
              "Đơn vị cung cấp dịch vụ hạ tầng (cloud, AI engine, bảo mật).",
              "Cơ quan nhà nước khi có yêu cầu hợp pháp.",
            ],
          },
        ],
      },
      {
        id: "bao-mat-du-lieu",
        title: "6. Bảo mật dữ liệu",
        blocks: [
          {
            type: "paragraph",
            text: "Chuẩn ISO 27001 (2022).",
          },
        ],
      },
      {
        id: "quyen-nguoi-dung",
        title: "7. Quyền của người dùng",
        blocks: [
          {
            type: "paragraph",
            text: "Bạn có quyền:",
          },
          {
            type: "list",
            items: [
              "Truy cập, chỉnh sửa dữ liệu cá nhân.",
              "Sử dụng nguồn dữ liệu trên nền tảng theo quy tắc và gói đăng ký của nền tảng.",
            ],
          },
        ],
      },
      {
        id: "thoi-gian-luu-tru",
        title: "8. Thời gian lưu trữ",
        blocks: [
          {
            type: "paragraph",
            text: "Dữ liệu được lưu trữ trong thời gian cần thiết để cung cấp dịch vụ và theo quy định pháp luật hiện hành.",
          },
        ],
      },
      {
        id: "thay-doi-chinh-sach",
        title: "9. Thay đổi chính sách",
        blocks: [
          {
            type: "paragraph",
            text: "Chúng tôi có thể cập nhật Chính sách Bảo mật để phù hợp với pháp luật và hoạt động kinh doanh. Phiên bản mới sẽ được công bố kèm ngày hiệu lực.",
          },
        ],
      },
    ],
    [contactEmail]
  );

  return (
    <div className="pp-container">
      <Row gutter={16} className="pp-grid">
        <Col xs={24} lg={24}>
          <Title level={1} className="pp-title">
            CHÍNH SÁCH BẢO MẬT
          </Title>
          <Text className="pp-subtle">
            Nền tảng: AI Procurement • Cập nhật: {lastUpdated}
          </Text>

          <Card size="small" className="pp-main-card">
            {sections.map((s, idx) => (
              <div key={s.id}>
                <section id={s.id} className="pp-section">
                  <Title level={3} className="pp-section-title">
                    {s.title}
                  </Title>

                  {s.blocks.map((b, i) => {
                    if (b.type === "paragraph") {
                      return (
                        <Paragraph key={i} className="pp-block">
                          {b.text}
                        </Paragraph>
                      );
                    }
                    if (b.type === "list") {
                      return (
                        <ul key={i} className="pp-list pp-block">
                          {b.items.map((it, j) => (
                            <li key={j}>{it}</li>
                          ))}
                        </ul>
                      );
                    }
                    if (b.type === "note") {
                      return (
                        <div key={i} className="pp-note pp-block">
                          {b.text}
                        </div>
                      );
                    }
                    return null;
                  })}
                </section>

                {idx < sections.length - 1 && (
                  <Divider className="pp-divider" />
                )}
              </div>
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PrivacyPolicy;
